import axios from "axios";
import ls from "local-storage";
import Cryptr from "cryptr";
import { BASE_URL, SECRET_KEY_ENCRYPT } from "@utils/config";
import { getLanguage } from "./index";

/**
 * Currency of the point of sale. One currency per caisse, no conversion: amounts are shown as stored.
 * Source: the account's Devise table (GET devise/:caisse), cached per caisse in localStorage so the
 * formatters stay synchronous. Not loaded yet -> the old rule (caisses 264 / 346 in euros), else MAD.
 */
export const CURRENCIES = {
  // symbol per language (fallback: fr); `before`: languages that write the symbol first (€12.50)
  MAD: { symbol: { fr: "DH", en: "DH", ar: "درهم" }, decimals: 2 },
  EUR: { symbol: { fr: "€" }, decimals: 2, before: ["en"] },
  USD: { symbol: { fr: "$" }, decimals: 2, before: ["en"] },
  GBP: { symbol: { fr: "£" }, decimals: 2, before: ["en"] },
  CAD: { symbol: { fr: "$ CA" }, decimals: 2 },
  XOF: { symbol: { fr: "FCFA" }, decimals: 0 },
  TND: { symbol: { fr: "DT", ar: "د.ت" }, decimals: 3 },
  DZD: { symbol: { fr: "DA", ar: "د.ج" }, decimals: 2 },
};
export const DEFAULT_CURRENCY = "MAD";

const LEGACY_EUR_CAISSES = ["264", "346"];
const cacheKey = (caisse) => `devise_${caisse}`;

let cryptr;
const currentCaisse = () => {
  try {
    const raw = ls.get("currentcaisse");
    if (!raw) return null;
    cryptr = cryptr || new Cryptr(SECRET_KEY_ENCRYPT);
    return cryptr.decrypt(raw);
  } catch (e) {
    return null;
  }
};

/** ISO code of the current point of sale: "MAD", "EUR"… */
export function getCurrency(caisse = currentCaisse()) {
  const cached = caisse ? ls.get(cacheKey(caisse)) : null;
  if (cached && CURRENCIES[cached]) return cached;
  return LEGACY_EUR_CAISSES.includes(String(caisse)) ? "EUR" : DEFAULT_CURRENCY;
}

const infoOf = (code) => CURRENCIES[code] || CURRENCIES[DEFAULT_CURRENCY];

/** "DH", "€", "درهم"… in the current language. */
export function currencySymbol(code = getCurrency()) {
  const { symbol } = infoOf(code);
  return symbol[getLanguage()] || symbol.fr;
}

export const currencyDecimals = (code = getCurrency()) => infoOf(code).decimals;
export const symbolBefore = (code = getCurrency()) => (infoOf(code).before || []).includes(getLanguage());

let loading = null;
/**
 * Reads the currency of the current caisse from the API (once per page load) and caches it.
 * When it differs from what was shown, pages listening to "lc:currency" re-render.
 */
export function loadCurrency() {
  const caisse = currentCaisse();
  if (!caisse) return Promise.resolve(getCurrency(caisse));
  if (!loading) {
    loading = axios
      .get(`${BASE_URL}devise/${caisse}`)
      .then((r) => {
        const code = r.data && CURRENCIES[r.data.code] ? r.data.code : DEFAULT_CURRENCY;
        const before = getCurrency(caisse);
        ls.set(cacheKey(caisse), code);
        if (code !== before) window.dispatchEvent(new CustomEvent("lc:currency", { detail: code }));
        return code;
      })
      .catch(() => {
        loading = null;
        return getCurrency(caisse);
      });
  }
  return loading;
}

/** Currency of any caisse of the account, straight from the API (Mon compte › Devise). */
export const fetchCurrency = (caisse) =>
  axios.get(`${BASE_URL}devise/${caisse}`).then((r) => (r.data && CURRENCIES[r.data.code] ? r.data.code : DEFAULT_CURRENCY));

/**
 * Saves the currency of a caisse (Devise table) and refreshes the cache: when it is the current caisse,
 * pages listening to "lc:currency" re-render with the new symbol.
 */
export async function saveCurrency(caisse, code) {
  await axios.put(`${BASE_URL}devise/${caisse}`, { code });
  const before = getCurrency(String(caisse));
  ls.set(cacheKey(caisse), code);
  if (String(caisse) === String(currentCaisse())) {
    loading = Promise.resolve(code);
    if (code !== before) window.dispatchEvent(new CustomEvent("lc:currency", { detail: code }));
  }
  return code;
}

export const isLockedCurrency = (caisse) => LEGACY_EUR_CAISSES.includes(String(caisse));
